import React, { useState } from "react";
import {
    Box,
    Card,
    CircularProgress,
    Typography,
    Stack,
    IconButton,
    Pagination,
    Divider
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import StoreIcon from "@mui/icons-material/Store";
import PeopleIcon from "@mui/icons-material/People";

import CatalogUtils from "../../../../utils/CatalogUtils";

const ITEMS_PER_PAGE = 10;

// Vista mobile de reponedores con diseño de cards paginadas
const ReponedoresMobileList = ({ reponedores, almacenes, usuarios, loading, onEdit, onDelete }) => {
    const [page, setPage] = useState(1);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress sx={{ color: "#cc071e" }} />
            </Box>
        );
    }

    const totalPages = Math.ceil(reponedores.length / ITEMS_PER_PAGE);
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const currentReponedores = reponedores.slice(startIndex, endIndex);

    const handleChangePage = (_event, value) => {
        setPage(value);
    };

    const formatAlmacenes = (codigos) => {
        if (!codigos?.length) return "-";
        return codigos.map(codigo =>
            CatalogUtils.getNombreAlmacen(codigo, almacenes)
        ).join(", ");
    };

    const formatUsuarios = (ids) => {
        if (!ids?.length) return "-";
        return ids.map(id =>
            CatalogUtils.getNombreEmpleado(id, usuarios)
        ).join(", ");
    };

    if (!reponedores.length) {
        return (
            <Box
                sx={{
                    textAlign: "center",
                    py: 6,
                    color: "#6c757d",
                }}
            >
                <Typography variant="body1">No hay asignaciones</Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Stack spacing={2}>
                {currentReponedores.map((reponedor) => {
                    return (
                        <Card
                            key={reponedor.id}
                            sx={{
                                borderLeft: `4px solid #cc071e`,
                                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                "&:hover": {
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                },
                            }}
                        >
                            <Box sx={{ p: 2 }}>
                                {/* Header: ID */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 1.5,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: 16,
                                            fontWeight: 700,
                                            color: "#212529",
                                        }}
                                    >
                                        #{reponedor.id}
                                    </Typography>
                                </Box>

                                <Divider sx={{ mb: 1.5 }} />

                                <Stack spacing={1}>
                                    {/* Almacenes */}
                                    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                                        <StoreIcon sx={{ fontSize: 16, color: "#6c757d", mt: 0.2 }} />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography sx={{ fontSize: 12, color: "#6c757d", fontWeight: 600, mb: 0.25 }}>
                                                Almacenes:
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: 13,
                                                    color: "#495057",
                                                    lineHeight: 1.4,
                                                    wordBreak: "break-word",
                                                }}
                                            >
                                                {formatAlmacenes(reponedor.almacenes)}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Empleados */}
                                    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                                        <PeopleIcon sx={{ fontSize: 16, color: "#6c757d", mt: 0.2 }} />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography sx={{ fontSize: 12, color: "#6c757d", fontWeight: 600, mb: 0.25 }}>
                                                Empleados:
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: 13,
                                                    color: "#495057",
                                                    lineHeight: 1.4,
                                                    wordBreak: "break-word",
                                                }}
                                            >
                                                {formatUsuarios(reponedor.usuarios)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Stack>

                                {/* Acciones */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        gap: 1,
                                        mt: 2,
                                        pt: 1.5,
                                        borderTop: "1px solid #e9ecef",
                                    }}
                                >
                                    <IconButton
                                        onClick={() => onEdit(reponedor.id)}
                                        size="small"
                                        sx={{
                                            color: "#495057",
                                            backgroundColor: "#f8f9fa",
                                            "&:hover": {
                                                backgroundColor: "#e9ecef",
                                            },
                                        }}
                                    >
                                        <EditIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                    <IconButton
                                        onClick={() => onDelete(reponedor.id)}
                                        size="small"
                                        sx={{
                                            color: "#dc3545",
                                            backgroundColor: "#f8f9fa",
                                            "&:hover": {
                                                backgroundColor: "#ffe5e8",
                                            },
                                        }}
                                    >
                                        <DeleteIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Box>
                            </Box>
                        </Card>
                    );
                })}
            </Stack>

            {/* Paginación */}
            {totalPages > 1 && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        mt: 3,
                        pb: 2,
                    }}
                >
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handleChangePage}
                        color="primary"
                        size="large"
                        sx={{
                            "& .MuiPaginationItem-root": {
                                fontSize: 14,
                            },
                            "& .Mui-selected": {
                                backgroundColor: "#cc071e !important",
                                color: "#fff",
                            },
                        }}
                    />
                </Box>
            )}
        </Box>
    );
};

export default ReponedoresMobileList;
